<template>
  <div>
    <textarea
      :value="value"
      v-on="listeners"
      ref="body"
      rows="1"
      :readonly="readonly"
      :class="['n-textarea', variant]"
      :maxlength="maxlength"
      :placeholder="placeholder"
    />
    <div v-if="maxlength && !readonly" class="maxlength">{{ length }}/{{ maxlength }}</div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'

@Component
export default class NTextArea extends Vue {
  @Prop() value!: string
  @Prop({ default: undefined, type: Boolean }) readonly?: boolean
  @Prop() maxlength?: number
  @Prop() placeholder?: string
  @Prop({ default: false, type: Boolean }) autofocus?: boolean
  @Prop({
    default: 'left',
    type: String,
    validator(value: string): boolean {
      return value === 'left' || value === 'center'
    }
  })
  variant!: 'left'

  @Ref('body')
  bodyRef!: HTMLTextAreaElement

  private lineHeight!: number

  private get listeners(): Vue['$listeners'] {
    return {
      ...this.$listeners,
      // Sized on input, not keydown: it runs after the text changed, and also covers paste, cut, drop and undo
      input: (event: InputEvent) => {
        this.updateBodyHeight()
        this.$emit('input', (event.target as HTMLTextAreaElement).value)
      },
      change: (event: InputEvent) => this.$emit('change', (event.target as HTMLTextAreaElement).value)
    }
  }

  mounted() {
    this.lineHeight = parseInt(window.getComputedStyle(this.bodyRef).lineHeight, 10) || 0
    this.$nextTick(() => {
      this.updateBodyHeight()
      if (this.autofocus) {
        this.bodyRef.focus()
      }
    })
  }

  @Watch('value') updateBodyHeight() {
    this.bodyRef.style.height = '0px'
    this.bodyRef.style.height = Math.max(this.bodyRef.scrollHeight, this.lineHeight) + 'px'
  }

  get length() {
    return this.value?.length ?? 0
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

textarea.n-textarea {
  @include notch-text-paragraph-regular-M;
  background-color: transparent;
  min-height: 0;
  height: auto;
  width: 100%;

  resize: none;

  overflow: hidden;

  border: none;
  outline: none;

  &[readonly='readonly'] {
    cursor: default;
  }

  &::placeholder {
    color: $notch-color-neutral-sand-500;
  }

  &.left {
    text-align: left;
  }

  &.center {
    text-align: center;
  }
}

.maxlength {
  @include notch-text-label-light-S;
  color: $notch-color-neutral-sand-600;
}
</style>
